import {
  Alert,
  Box,
  Button,
  InputAdornment,
  Link,
  Paper,
  Snackbar,
  TextField,
  Tooltip,
  Typography
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import React, { useContext, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  updateAccount,
  validatePms as validatePmsHandler,
  validateCrm as validateCrmHandler
} from '../../../Redux/Actions/SelfServe';
import {
  validatePmsError,
  validateCrmError
} from '../../../Redux/Reducer/SelfServe';
import { GlobalContext } from '../../../utils/ContextProviders/GlobalContextProvider';

const fieldLabelSx = {
  fontFamily: 'Inter, sans-serif',
  fontWeight: 500,
  fontSize: '16px',
  lineHeight: '19px',
  color: 'text.secondary',
  mb: '6px'
};

const PMS = ({ info, infoPopUPHandler, tenantId }) => {
  const dispatch = useDispatch();
  const { userData } = useContext(GlobalContext);

  const selfServe = useSelector(state => state.SelfServe);

  const [verify, setVerify] = useState(false);
  const [verifiedSnackbar, setVerifiedSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const integrationSettings =
    selfServe?.userData?.data?.integrationSettings || {};

  const buildFormState = () => ({
    tenantId,
    pms: integrationSettings.pms,
    username: integrationSettings?.username || integrationSettings?.userName,
    password: integrationSettings.password,
    url: integrationSettings?.yardiUrl || integrationSettings?.url || '',
    yardiId: integrationSettings?.database || '',
    pmcId: integrationSettings?.pmcId || '',
    orgName: integrationSettings?.orgName,
    companyId: integrationSettings?.companyId,
    CompanyCode: integrationSettings?.CompanyCode || '',
    rentcafeApiV2Token: integrationSettings?.rentcafeApiV2Token || ''
  });

  const [form, setForm] = useState(buildFormState);

  useEffect(() => {
    setForm(buildFormState());
  }, [selfServe?.userData?.data, tenantId]);

  const {
    username,
    password,
    url,
    sqlDataBase,
    pmcId,
    yardiId,
    orgName,
    companyId,
    CompanyCode,
    rentcafeApiV2Token
  } = form;
  const pmsValue = integrationSettings && integrationSettings?.pms;
  const enableKnockCrm = selfServe?.userData?.data?.enableKnockCRM;
  const enableRentCafeCRM = selfServe?.userData?.data?.enableRentCafeCRM;
  const realPage =
    pmsValue?.toLowerCase()?.replace(/\s/g, '') === 'realpage' &&
    !enableKnockCrm;
  const realpageKnockCRM =
    pmsValue?.toLowerCase()?.replace(/\s/g, '') === 'realpage' &&
    enableKnockCrm;
  const yardi = pmsValue?.toLowerCase() === 'yardi' && !enableKnockCrm;
  const yardiKnockCRM = pmsValue?.toLowerCase() === 'yardi' && enableKnockCrm;
  const entrata = pmsValue?.toLowerCase() === 'entrata';
  const isYardi = yardi;
  const validatePms = selfServe?.validatePms;
  const { verified, error } = validatePms;
  const validateCrm = selfServe?.validateCrm;
  const { verified: crmVerified, error: crmError } = validateCrm;

  const handleForm = e => {
    const { value, name } = e.target;
    if (
      name === 'pmcId' ||
      (entrata && name === 'orgName') ||
      ((yardi || yardiKnockCRM) &&
        ['username', 'password', 'url', 'yardiId', 'companyId'].includes(
          name
        )) ||
      (realpageKnockCRM && name === 'companyId')
    ) {
      dispatch(validatePmsError());
    }
    if (['CompanyCode', 'rentcafeApiV2Token'].includes(name)) {
      dispatch(validateCrmError());
    }
    setForm(prev => {
      return {
        ...prev,
        [name]: value
      };
    });
  };

  const validatePmsErrorCall = status => {
    dispatch(validatePmsError(status));
  };
  const validateCrmErrorCall = status => {
    dispatch(validateCrmError(status));
  };
  const validatePmsAction = () => {
    const pms = pmsValue?.toLowerCase().replace(/ /g, '');
    if (yardi || yardiKnockCRM) {
      const payload = {
        pms: 'yardi',
        integrationSettings: {
          pms: 'yardi',
          yardiUrl: url,
          yardiId: yardiId,
          userName: username,
          password: password,
          database: yardiId,
          platform: 'SQL Server',
          companyId,
          isNewVersion: true
        }
      };
      dispatch(
        validatePmsHandler({
          payload,
          clientId: tenantId,
          errorCheck: validatePmsErrorCall
        })
      );
    }
    if (realPage || realpageKnockCRM) {
      const payload = {
        pms,
        integrationSettings: {
          pmcId
        }
      };
      dispatch(
        validatePmsHandler({
          payload,
          clientId: tenantId,
          errorCheck: validatePmsErrorCall
        })
      );
    }
    if (entrata) {
      const payload = {
        pms: 'entrata',
        integrationSettings: {
          pms: 'entrata',
          orgName: orgName,
          isNewVersion: true
        }
      };
      dispatch(
        validatePmsHandler({
          payload,
          clientId: tenantId,
          errorCheck: validatePmsErrorCall
        })
      );
    }
  };

  const validateCrmAction = () => {
    dispatch(
      validateCrmHandler({
        payload: {
          crm: 'rentcafe',
          crmSettings: {
            apiToken: rentcafeApiV2Token,
            companyCode: CompanyCode
          }
        },
        clientId: tenantId,
        errorCheck: validateCrmErrorCall
      })
    );
  };

  const saveClickHandler = () => {
    const needsCrmVerify =
      enableRentCafeCRM && CompanyCode && rentcafeApiV2Token;
    const pmsNeedsVerify = !verified || error;
    const crmNeedsVerify = needsCrmVerify && (!crmVerified || crmError);

    if (pmsNeedsVerify || crmNeedsVerify) {
      if (pmsNeedsVerify) validatePmsAction();
      if (crmNeedsVerify) validateCrmAction();
      return;
    }

    if (userData && Object.keys(integrationSettings).length) {
      const initialItem = {
        tenantId,
        pms: pmsValue?.toLowerCase().replace(/ /g, ''),
        isIntegrationConfigured: true
      };

      if (initialItem.pms === 'entrata' || yardiKnockCRM || realpageKnockCRM) {
        initialItem['isNewVersion'] = true;
      }

      const payload = Object.keys(form).reduce((acc, item) => {
        return form[item] ? { ...acc, [item]: form[item] } : acc;
      }, initialItem);
      dispatch(updateAccount({ payload, showNotification }));
    }
  };

  useEffect(() => {
    let shouldVerify = false;
    if (realPage && form['pmcId']) {
      shouldVerify = true;
    }
    if (realpageKnockCRM && form['pmcId'] && form['companyId']) {
      shouldVerify = true;
    }
    if (entrata && form['orgName']) {
      shouldVerify = true;
    }
    if (
      yardi &&
      form['password'] &&
      form['url'] &&
      form['username'] &&
      form['yardiId']
    ) {
      shouldVerify = true;
    }
    if (
      yardiKnockCRM &&
      form['password'] &&
      form['url'] &&
      form['username'] &&
      form['yardiId'] &&
      form['companyId']
    ) {
      shouldVerify = true;
    }
    setVerify(shouldVerify);
  }, [form, realPage, entrata, yardi, verified, error, integrationSettings]);

  useEffect(() => {
    if (verified && !error) {
      setSnackbarMessage('Client configuration verified');
      setVerifiedSnackbar(true);
    }
  }, [verified, error]);

  useEffect(() => {
    if (crmVerified && !crmError) {
      setSnackbarMessage('RentCafe CRM credentials verified');
      setVerifiedSnackbar(true);
    }
  }, [crmVerified, crmError]);

  useEffect(() => {
    dispatch(validatePmsError());
    dispatch(validateCrmError());
    return () => {
      infoPopUPHandler(false);
    };
  }, []);

  const showNotification = () => {
    setSnackbarMessage('Client configuration saved');
    setVerifiedSnackbar(true);
  };

  const VerifyAdornment = ({ disabledTooltip }) => {
    if (!error && !verified) {
      const link = (
        <Link
          component="button"
          underline="hover"
          onClick={disabledTooltip ? undefined : validatePmsAction}
          sx={{
            fontSize: '14px',
            fontWeight: 500,
            ...(disabledTooltip && {
              color: 'text.disabled',
              cursor: 'default'
            })
          }}
        >
          Verify
        </Link>
      );
      return (
        <InputAdornment position="end">
          {disabledTooltip ? (
            <Tooltip title={disabledTooltip} arrow placement="right">
              <span>{link}</span>
            </Tooltip>
          ) : (
            link
          )}
        </InputAdornment>
      );
    }
    if (error) {
      return (
        <InputAdornment position="end">
          <ErrorOutlineIcon sx={{ color: '#FF4D00' }} />
        </InputAdornment>
      );
    }
    if (verified && !error) {
      return (
        <InputAdornment position="end">
          <CheckCircleIcon sx={{ color: '#50BE77' }} />
        </InputAdornment>
      );
    }
    return null;
  };

  const CrmVerifyAdornment = ({ disabledTooltip }) => {
    if (!crmError && !crmVerified) {
      const link = (
        <Link
          component="button"
          underline="hover"
          onClick={disabledTooltip ? undefined : validateCrmAction}
          sx={{
            fontSize: '14px',
            fontWeight: 500,
            ...(disabledTooltip && {
              color: 'text.disabled',
              cursor: 'default'
            })
          }}
        >
          Verify
        </Link>
      );
      return (
        <InputAdornment position="end">
          {disabledTooltip ? (
            <Tooltip title={disabledTooltip} arrow placement="right">
              <span>{link}</span>
            </Tooltip>
          ) : (
            link
          )}
        </InputAdornment>
      );
    }
    if (crmError) {
      return (
        <InputAdornment position="end">
          <ErrorOutlineIcon sx={{ color: '#FF4D00' }} />
        </InputAdornment>
      );
    }
    if (crmVerified && !crmError) {
      return (
        <InputAdornment position="end">
          <CheckCircleIcon sx={{ color: '#50BE77' }} />
        </InputAdornment>
      );
    }
    return null;
  };

  const DiagnoseLink = () => (
    <Link
      component="button"
      underline="hover"
      onClick={() =>
        window.open(
          import.meta.env.VITE_YARDI_RETOOL_DIAGNOSE_URL ?? '',
          'WindowName',
          'noopener'
        )
      }
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: '4px',
        fontSize: '14px',
        mt: '8px'
      }}
    >
      Diagnose
      <OpenInNewIcon sx={{ fontSize: '16px' }} />
    </Link>
  );

  return (
    <Paper sx={{ borderRadius: '4px', p: '24px' }} elevation={0}>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
        {/* Entrata: Organization Name */}
        {entrata && (
          <Box>
            <Typography sx={fieldLabelSx}>Organization Name</Typography>
            <TextField
              size="small"
              value={orgName}
              onChange={handleForm}
              name="orgName"
              placeholder="Enter Organization Name"
              sx={{ width: '350px' }}
            />
          </Box>
        )}

        {/* Entrata & Yardi: Username / Password */}
        {isYardi && (
          <Box>
            <Box sx={{ display: 'flex', gap: '24px' }}>
              <Box>
                <Typography sx={fieldLabelSx}>User Name</Typography>
                <TextField
                  size="small"
                  value={username}
                  onChange={handleForm}
                  name="username"
                  placeholder="Enter User Name"
                  sx={{ width: '350px' }}
                />
              </Box>
              <Box>
                <Typography sx={fieldLabelSx}>Password</Typography>
                <TextField
                  size="small"
                  value={password}
                  onChange={handleForm}
                  name="password"
                  placeholder="Enter Password"
                  sx={{ width: '350px' }}
                  type="password"
                />
              </Box>
            </Box>
            {yardi && <DiagnoseLink />}
          </Box>
        )}

        {/* Entrata & Yardi: URL + SQL Database */}
        {isYardi && (
          <Box sx={{ display: 'flex', gap: '24px' }}>
            <Box>
              <Typography sx={fieldLabelSx}>URL</Typography>
              <TextField
                size="small"
                value={url}
                onChange={handleForm}
                name="url"
                placeholder="Enter URL"
                sx={{ width: '350px' }}
              />
            </Box>
            {yardi && (
              <Box>
                <Typography sx={fieldLabelSx}>SQL Database</Typography>
                <TextField
                  size="small"
                  value={yardiId}
                  onChange={handleForm}
                  name="yardiId"
                  placeholder="Enter SQL Database"
                  sx={{ width: '350px' }}
                />
              </Box>
            )}
          </Box>
        )}

        {/* RealPage: PMC Id */}
        {realPage && (
          <Box>
            <Typography sx={fieldLabelSx}>PMC Id</Typography>
            <TextField
              size="small"
              onChange={handleForm}
              value={pmcId}
              name="pmcId"
              placeholder="Enter PMC Id"
              sx={{ width: '350px' }}
              InputProps={{
                endAdornment: (
                  <VerifyAdornment
                    disabledTooltip={
                      !form['pmcId'] ? 'Enter PMC Id to verify' : undefined
                    }
                  />
                )
              }}
            />
          </Box>
        )}

        {/* RealPage + KnockCRM: PMC Id + Company Id */}
        {realpageKnockCRM && (
          <>
            <Box>
              <Typography sx={fieldLabelSx}>PMC Id</Typography>
              <TextField
                size="small"
                onChange={handleForm}
                value={pmcId}
                name="pmcId"
                placeholder="Enter PMC Id"
                sx={{ width: '350px' }}
                InputProps={{
                  endAdornment: (
                    <VerifyAdornment
                      disabledTooltip={
                        !form['pmcId'] ? 'Enter PMC Id to verify' : undefined
                      }
                    />
                  )
                }}
              />
            </Box>
            <Box>
              <Typography sx={fieldLabelSx}>Company Id</Typography>
              <TextField
                size="small"
                onChange={handleForm}
                value={companyId}
                name="companyId"
                placeholder="Enter Company Id"
                sx={{ width: '350px' }}
              />
            </Box>
          </>
        )}

        {/* Yardi + KnockCRM: Username / Password / URL / SQL / Company Id */}
        {yardiKnockCRM && (
          <>
            <Box>
              <Box sx={{ display: 'flex', gap: '24px' }}>
                <Box>
                  <Typography sx={fieldLabelSx}>Username</Typography>
                  <TextField
                    size="small"
                    value={username}
                    onChange={handleForm}
                    name="username"
                    placeholder="Enter User Name"
                    sx={{ width: '350px' }}
                  />
                </Box>
                <Box>
                  <Typography sx={fieldLabelSx}>Password</Typography>
                  <TextField
                    size="small"
                    value={password}
                    onChange={handleForm}
                    name="password"
                    placeholder="Enter Password"
                    sx={{ width: '350px' }}
                    type="password"
                  />
                </Box>
              </Box>
              <DiagnoseLink />
            </Box>
            <Box sx={{ display: 'flex', gap: '24px' }}>
              <Box>
                <Typography sx={fieldLabelSx}>URL</Typography>
                <TextField
                  size="small"
                  value={url}
                  onChange={handleForm}
                  name="url"
                  placeholder="Enter URL"
                  sx={{ width: '350px' }}
                />
              </Box>
              <Box>
                <Typography sx={fieldLabelSx}>SQL Database</Typography>
                <TextField
                  size="small"
                  value={yardiId}
                  onChange={handleForm}
                  name="yardiId"
                  placeholder="Enter SQL Database"
                  sx={{ width: '350px' }}
                />
              </Box>
            </Box>
            <Box>
              <Typography sx={fieldLabelSx}>Company Id</Typography>
              <TextField
                size="small"
                value={companyId}
                onChange={handleForm}
                name="companyId"
                placeholder="Enter Company Id"
                sx={{ width: '350px' }}
              />
            </Box>
          </>
        )}

        {pmsValue?.toLowerCase() === 'yardi' && enableRentCafeCRM && (
          <Box sx={{ display: 'flex', gap: '24px' }}>
            <Box>
              <Typography sx={fieldLabelSx}>
                Company Code{' '}
                <Typography
                  component="span"
                  sx={{ color: 'text.disabled', fontSize: '13px' }}
                >
                  (optional)
                </Typography>
              </Typography>
              <TextField
                size="small"
                value={CompanyCode}
                onChange={handleForm}
                name="CompanyCode"
                placeholder="Enter Company Code"
                sx={{ width: '350px' }}
              />
            </Box>
            <Box>
              <Typography sx={fieldLabelSx}>API Token</Typography>
              <TextField
                size="small"
                value={rentcafeApiV2Token}
                onChange={handleForm}
                name="rentcafeApiV2Token"
                placeholder="Enter API Token"
                sx={{ width: '350px' }}
              />
            </Box>
          </Box>
        )}
      </Box>

      {/* Save / Verify Button */}
      <Box sx={{ mt: '32px' }}>
        <Tooltip
          title={
            !verify && realpageKnockCRM
              ? 'Both PMC Id and Company Id are required to verify'
              : ''
          }
          arrow
          placement="right"
        >
          <span>
            <Button
              onClick={saveClickHandler}
              variant="contained"
              disabled={!verify}
            >
              {verified &&
              !error &&
              (!(enableRentCafeCRM && CompanyCode && rentcafeApiV2Token) ||
                (crmVerified && !crmError))
                ? 'Save'
                : 'Verify'}
            </Button>
          </span>
        </Tooltip>
      </Box>

      <Snackbar
        open={verifiedSnackbar}
        autoHideDuration={4000}
        onClose={() => setVerifiedSnackbar(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert
          severity="success"
          variant="filled"
          onClose={() => setVerifiedSnackbar(false)}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Paper>
  );
};

export default PMS;
